/**
 * 스마트빌 대량 업로드 고정서식 파일 만들기 — 두 가지(서식이 다르다).
 * - 'tax' 세금계산서 대량 업로드(개선요청 #14) — 요청 첨부 「세금계산서 대량 업로드 파일_스마트빌샘플.xlsx」 그대로, 30칸 · 시트 `1.세금계산서`.
 * - 'statement' 거래명세서 대량 업로드(게시판 #120) — 요청 첨부 「99_DTT_Excel_Sample.xls」 그대로, 35칸 · 시트 `1.거래명세서`.
 * 둘 다 안내 3줄 · 빈 줄 · 묶음 머리줄(병합 3) · 칸 머리줄 · 7행부터 자료. 시트는 하나만(안내 2번 — 다른 시트는 지우고 올린다).
 * 줄 내용은 서버 `smartbillRow`·`statementRow`(functions/src/invoiceParse.ts)가 만든다. 여기선 서식에 담아 내려받기만 한다.
 * 스마트빌 계정이 법인별이라 **법인마다 한 파일**.
 */

export type SmartbillKind = 'tax' | 'statement';

const NOTES = [
  '* 수량 및 단가를 제외한 모든 금액 항목은 반올림 되어 저장됩니다.',
  '* 현재 시트(Sheet)를 제외한 모든 시트를 삭제 후 엑셀을 업로드하여 주시기 바랍니다. ',
  '* 엑셀의 셀 서식은 텍스트로 작성하고, 함수나 수식이 들어가지 않도록 작성해주세요.',
];

const TAX_HEADERS = [
  '1.구분자\n(형식 : $$$111)', '2. 거래처 \n사업자번호\n(개인인 경우 주민등록번호)', '3.종사업장번호', '4. 거래처 담당자 \n(이메일)',
  '5.담당자2(담당자명)', '6.담당자2\n(이메일)', '7. 종류\n(과세인 경우 T,\n영세인 경우 P,\n면세인 경우 F)', '8. 관리번호(일련번호)',
  '9. 일자', '10. 공급가액 ', '11. 세액', '12. 합계', '13. 비고1', '14. 비고2', '15. 비고3',
  '16. 청구/영수\n(청구인 경우 18,\n영수인 경우 1)', '17.현금', '18.수표', '19.어음', '20.외상미수금',
  '21.수정코드\n(기재사항의 착오·정정 : 01\n공급가액 변동 : 02\n환입 : 03\n계약의 해제 : 04\n내국신용장 개설입 : 05\n착오에 의한 이중발급 등 : 06)',
  '22.당초승인번호', '23.월/일', '24.품목명', '25.규격', '26.수량', '27.단가', '28.공급가액', '29.세액', '30.비고',
];

const STATEMENT_HEADERS = [
  '1.구분자\n(형식 : $$$111)', '2. 거래처 \n사업자번호\n(개인인 경우 주민등록번호)', '3. 종사업장번호', '4.거래처 \n담당자 \n(이메일)',
  '5.담당자2(담당자명)', '6.담당자2(이메일)', '7. 종류\n(과세인 경우 T,\n영세인 경우 P,\n면세인 경우 F)', '8. 관리번호(일련번호)',
  '9. 일자', '10. 공급가액 ', '11. 세액', '12. 합계', '13. 비고1', '14. 비고2', '15. 비고3',
  '16. 청구/영수\n(청구인 경우 18,\n영수인 경우 1)', '17.현금', '18.수표', '19.어음', '20.외상미수금',
  '21. 기타번호1', '22. 기타번호2', '23. 기타번호3', '24.기타번호4',
  '25.수정코드\n(기재사항의 착오·정정 : 01\n공급가액 변동 : 02\n환입 : 03\n계약의 해제 : 04\n내국신용장 개설입 : 05\n착오에 의한 이중발급 등 : 06)',
  '26.당초승인번호', '27.월/일', '28.코드', '29.품목명', '30.규격', '31.수량', '32.단가', '33.공급가액', '34.세액', '35.비고',
];

/** 서식별 차이 — 칸 머리 · 묶음 머리(칸 위치 → 글자, 병합 끝) · 시트명 · 파일명 앞말 · 넓은 칸. */
const FORMS: Record<SmartbillKind, { headers: string[]; groups: [number, string, number][]; sheet: string; prefix: string; wide: number[] }> = {
  tax: {
    headers: TAX_HEADERS, sheet: '1.세금계산서', prefix: '스마트빌_세금계산서', wide: [0, 1, 3, 12, 23],
    groups: [[0, '시스템 정보', 0], [1, '거래처 정보', 5], [6, '세금계산서 헤더정보', 21], [22, '품목정보 (1 ~ 99개)', 29]],
  },
  statement: {
    headers: STATEMENT_HEADERS, sheet: '1.거래명세서', prefix: '스마트빌_거래명세서', wide: [0, 1, 3, 12, 28],
    groups: [[0, '시스템 정보', 0], [1, '거래처 정보', 5], [6, '거래명세서 헤더정보', 25], [26, '품목정보', 34]],
  },
};

/** 머리 6줄 + 자료 줄로 시트 하나만 든 통합문서를 만든다(검증 스크립트도 이걸 쓴다). */
export function buildSmartbillBook(XLSX: typeof import('xlsx'), kind: SmartbillKind, rows: (string | number)[][]) {
  const f = FORMS[kind];
  const width = f.headers.length;
  const group: string[] = new Array(width).fill('');
  for (const [c, text] of f.groups) group[c] = text;
  const aoa: (string | number)[][] = [...NOTES.map((n) => [n]), [], group, f.headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!merges'] = f.groups.filter(([c, , end]) => end > c).map(([c, , end]) => ({ s: { r: 4, c }, e: { r: 4, c: end } }));
  ws['!cols'] = f.headers.map((_, i) => ({ wch: f.wide.includes(i) ? 24 : 12 }));
  // 글자 칸은 텍스트 서식(@) — 서식 안내 3번. 사업자번호·일자·월/일이 숫자로 바뀌면 앞자리 0 이 사라진다.
  for (let r = 6; r < aoa.length; r++) {
    for (let c = 0; c < width; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell && cell.t === 's') cell.z = '@';
    }
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, f.sheet);
  return wb;
}

export async function downloadSmartbill(kind: SmartbillKind, corpName: string, rows: (string | number)[][], ymd: string) {
  const XLSX = await import('xlsx');
  XLSX.writeFile(buildSmartbillBook(XLSX, kind, rows), `${FORMS[kind].prefix}_${corpName}_${ymd}.xlsx`);
}
